import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { DayPicker } from 'react-day-picker';
import { ALL_PERIODS, getPeriodName, getPropertiesForPeriod } from '../../periods';
import moment from 'moment';

class DataExplorerPeriodSelectorComponent extends Component {
    constructor(props) {
        super(props);
        // Inicializar el estado con initialPeriodState si está disponible, sino usar valores por defecto
        const initialState = props.initialPeriodState || {
            selectedPeriod: 'LAST_7_DAYS',
            range: { from: new Date(), to: new Date() },
        };
        
        // Convertir fechas de string a Date si es necesario
        const range = initialState.range || { from: new Date(), to: new Date() };
        const processedRange = {
            from: range.from 
                ? (range.from instanceof Date ? range.from : new Date(range.from))
                : new Date(),
            to: range.to 
                ? (range.to instanceof Date ? range.to : new Date(range.to))
                : new Date(),
        };
        
        this.state = {
            selectedPeriod: initialState.selectedPeriod || 'LAST_7_DAYS',
            range: processedRange,
            isDatePickerOpen: false,
        };
        this.datePickerRef = React.createRef();
        this.handlePeriodChange = this.handlePeriodChange.bind(this);
        this.handleDateRangeSelect = this.handleDateRangeSelect.bind(this);
        this.handleClickOutside = this.handleClickOutside.bind(this);
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
        // Esperar un momento para dar tiempo a que el padre restaure el estado si es necesario
        // Si hay initialPeriodState, ya se inicializó correctamente en el constructor
        setTimeout(() => {
            // Solo llamar a onChange si no hay un initialPeriodState que se va a aplicar
            // Si hay initialPeriodState, el componenteDidUpdate lo manejará
            if (!this.props.initialPeriodState || Object.keys(this.props.initialPeriodState).length === 0) {
                this.props.onChange(this.state);
            } else {
                // Si hay initialPeriodState, asegurarse de que onChange se llame con el estado correcto
                this.props.onChange(this.state);
            }
        }, 50);
    }

    componentDidUpdate(prevProps) {
        // Si cambia el initialPeriodState, actualizar el estado
        // Solo actualizar si el initialPeriodState cambió de null/undefined a un valor, o si cambió de valor
        const hadInitialState = prevProps.initialPeriodState && Object.keys(prevProps.initialPeriodState).length > 0;
        const hasInitialState = this.props.initialPeriodState && Object.keys(this.props.initialPeriodState).length > 0;
        
        if (hasInitialState && (!hadInitialState || 
            JSON.stringify(prevProps.initialPeriodState) !== JSON.stringify(this.props.initialPeriodState))) {
            // Convertir fechas de string a Date si es necesario
            const newState = {
                selectedPeriod: this.props.initialPeriodState.selectedPeriod,
                range: {
                    from: this.props.initialPeriodState.range?.from 
                        ? (this.props.initialPeriodState.range.from instanceof Date 
                            ? this.props.initialPeriodState.range.from 
                            : new Date(this.props.initialPeriodState.range.from))
                        : new Date(),
                    to: this.props.initialPeriodState.range?.to 
                        ? (this.props.initialPeriodState.range.to instanceof Date 
                            ? this.props.initialPeriodState.range.to 
                            : new Date(this.props.initialPeriodState.range.to))
                        : new Date(),
                },
                isDatePickerOpen: this.state.isDatePickerOpen,
            };
            
            this.setState(newState, () => {
                this.props.onChange(this.state);
            });
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside(event) {
        if (this.datePickerRef.current && !this.datePickerRef.current.contains(event.target)) {
            this.setState({ isDatePickerOpen: false });
        }
    }

    handlePeriodChange(event) {
        const selectedPeriod = event.target.value;
        if (selectedPeriod === 'CUSTOM') {
            this.setState({ selectedPeriod, isDatePickerOpen: true });
        } else {
            this.setState({ selectedPeriod, isDatePickerOpen: false }, () => {
                this.props.onChange(this.state);
            });
        }
    }

    getCustomPeriodText() {
        const { t, i18n } = this.props;
        const { range, selectedPeriod } = this.state;
        const customText = t('periods.custom');
        
        // Si está seleccionado "Personalizado", mostrar solo el rango de fechas
        if (selectedPeriod === 'CUSTOM' && range.from && range.to) {
            const fromString = moment(range.from).format('DD MMM YYYY');
            const toString = moment(range.to).format('DD MMM YYYY');
            const separator = i18n.language === 'en' ? ' to ' : ' a ';
            return `${fromString}${separator}${toString}`;
        }
        
        // Si no está seleccionado, mostrar solo "Personalizado"
        return customText;
    }

    handleDateRangeSelect(day) {
        if (!day) return;

        const { range } = this.state;
        const newRange = { from: range.from, to: range.to };

        if (!newRange.from || newRange.to) {
            newRange.from = day;
            newRange.to = undefined;
        } else if (moment(day).isSameOrAfter(newRange.from, 'day')) {
            newRange.to = day;
        } else {
            newRange.from = day;
        }

        if (newRange.from && newRange.to) {
            this.setState({ range: newRange, isDatePickerOpen: false }, () => {
                this.props.onChange(this.state);
            });
        } else {
            this.setState({ range: newRange });
        }
    }

    render() {
        const { t } = this.props;
        const periodProperties = getPropertiesForPeriod(t);
        const availablePeriods = ALL_PERIODS.filter(p => 
            periodProperties[p]?.depends_of_other_period === false && 
            !['THIS_PERIOD', 'THIS_PERIOD_TILL_YESTERDAY'].includes(p)
        );
        
        const rangeKey = this.state.range.from && this.state.range.to 
            ? `${moment(this.state.range.from).format('YYYY-MM-DD')}_${moment(this.state.range.to).format('YYYY-MM-DD')}` 
            : 'no-range';

        return (
            <div className="period-selector" style={{ position: 'relative' }}>
                <select 
                    key={`${this.state.selectedPeriod}_${rangeKey}`}
                    value={this.state.selectedPeriod} 
                    onChange={this.handlePeriodChange} 
                    className="form-select"
                    onFocus={(e) => {
                        if (this.state.selectedPeriod === 'CUSTOM') {
                            e.target.blur();
                            this.setState({ isDatePickerOpen: !this.state.isDatePickerOpen });
                        }
                    }}
                >
                    {availablePeriods.map(period => (
                        <option key={period} value={period}>{getPeriodName(period, t)}</option>
                    ))}
                    <option value="CUSTOM">{this.getCustomPeriodText()}</option>
                </select>

                {this.state.selectedPeriod === 'CUSTOM' && this.state.isDatePickerOpen && (
                    <div ref={this.datePickerRef} className="date-picker-popover" style={{ position: 'absolute', top: '100%', right: 0, zIndex: 1000, marginTop: '5px' }}>
                        <DayPicker
                            mode="range"
                            selected={this.state.range}
                            onSelect={(range, day) => this.handleDateRangeSelect(day)}
                            numberOfMonths={2}
                        />
                    </div>
                )}
            </div>
        );
    }
}

export const DataExplorerPeriodSelector = withTranslation()(DataExplorerPeriodSelectorComponent);

